import { useCallback, useEffect, useRef, useState } from "react";

import { websocketUrl } from "../api/client";

interface PendingCall {
  resolve: (data: unknown) => void;
  reject: (error: Error) => void;
  timer: ReturnType<typeof setTimeout>;
  sentAt: number;
}

const BASE_DELAY_MS = 500;
const MAX_DELAY_MS = 10_000;
/** Server command timeout is 20s; a little slack so the agent's own error wins. */
const COMMAND_TIMEOUT_MS = 22_000;
/** How often the panel measures its own round trip to the server. */
export const PING_INTERVAL_MS = 5_000;
const PING_TIMEOUT_MS = 10_000;
/** Frames inside this window count towards the displayed rate. */
const FPS_WINDOW_MS = 2_000;
/** Connected, but no frame for this long: the stream has stalled. */
export const STALL_AFTER_MS = 3_000;
/** Connected, never a frame, for this long: say so instead of "waiting". */
export const FIRST_FRAME_TIMEOUT_MS = 8_000;
/** The HUD refreshes at this cadence even when nothing arrives. */
const TICK_MS = 1_000;

export interface PanelStats {
  /** Frames delivered per second over the last two seconds. */
  fps: number;
  /** Browser → server → browser, from the last ping. */
  serverRttMs: number | null;
  /** Browser → phone → browser for the last device command. */
  commandRttMs: number | null;
  /**
   * Capture on the phone → arrival here, for the last frame. Approximate:
   * both ends put their clocks against the server's at connect, each with
   * about one way of latency of error, and those errors largely cancel.
   */
  frameLagMs: number | null;
  /** When the last frame arrived (`Date.now()`), null before the first. */
  lastFrameAt: number | null;
}

/**
 * The phone's word on whether a picture is coming, from the server's hello
 * and from every change afterwards. `reason` is what the panel switches on;
 * `inferred` marks a server guess for an agent that has not said anything.
 */
export interface CaptureState {
  available: boolean;
  backend: string;
  reason: string;
  message: string;
  max_fps?: number | null;
  inferred?: boolean;
}

export interface FrameHeader {
  t?: string;
  captured_at_ms?: number;
  captured_server_ms?: number;
  relayed_at_ms?: number;
  recording?: boolean;
  seq?: number;
  /** The screen's corner radius as a fraction of its short edge, as the
   *  phone measures it (a Pixel is around 0.09; the emulator says 0). */
  corner_radius?: number;
}

const EMPTY_STATS: PanelStats = {
  fps: 0,
  serverRttMs: null,
  commandRttMs: null,
  frameLagMs: null,
  lastFrameAt: null,
};

/** `[u32 BE header length][JSON header][JPEG]`, as the server relays it. */
export function decodeLiveFrame(
  buffer: ArrayBuffer,
): { header: FrameHeader; jpeg: Uint8Array } | null {
  if (buffer.byteLength < 4) return null;
  const length = new DataView(buffer).getUint32(0);
  if (4 + length > buffer.byteLength) return null;
  try {
    const text = new TextDecoder().decode(new Uint8Array(buffer, 4, length));
    return { header: JSON.parse(text) as FrameHeader, jpeg: new Uint8Array(buffer, 4 + length) };
  } catch {
    return null;
  }
}

/** An `<img>` source for raw JPEG bytes: an object URL where the browser has
 *  them, otherwise (test runners) a data URL. */
function frameSource(jpeg: Uint8Array): string {
  if (typeof URL.createObjectURL === "function") {
    return URL.createObjectURL(new Blob([new Uint8Array(jpeg)], { type: "image/jpeg" }));
  }
  let binary = "";
  for (let i = 0; i < jpeg.length; i++) binary += String.fromCharCode(jpeg[i]);
  return `data:image/jpeg;base64,${btoa(binary)}`;
}

function releaseSource(url: string | null) {
  if (url?.startsWith("blob:") && typeof URL.revokeObjectURL === "function") {
    URL.revokeObjectURL(url);
  }
}

/**
 * The panel's transport: frames stream in, commands go out over the same
 * socket, and each command's result is matched back to its caller by id.
 *
 * The socket is the flakiest part of the panel — the phone drops off Wi-Fi,
 * the server redeploys, the laptop sleeps — so a close is never the end: the
 * hook reconnects with capped backoff until the stream comes back, and only
 * gives up when retrying cannot help (bad credentials). The server closes the
 * panel when the phone itself drops, so "reconnecting" here is also how the
 * panel waits for a phone to come back.
 *
 * It also keeps score: delivered frame rate, the round trip to the server,
 * the round trip of the last command through the phone, and how old the
 * picture on screen is — the numbers that turn "feels slow" into a diagnosis.
 */
export function useDevicePanel(deviceId: string) {
  const [frame, setFrame] = useState<string | null>(null);
  const [capture, setCapture] = useState<CaptureState | null>(null);
  const [firstFrameLate, setFirstFrameLate] = useState(false);
  const [connected, setConnected] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);
  /** The server closed us with 4409: the *phone* is offline, and the hook
   *  keeps redialling until it is back. Rendered inside the dead screen, not
   *  as a page-level alert — a banner here mounts and unmounts on every
   *  reconnect attempt and makes the whole panel jump under it. */
  const [deviceOffline, setDeviceOffline] = useState(false);
  const [stalled, setStalled] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<PanelStats>(EMPTY_STATS);
  // How rounded the phone's screen really is, from the last frame that said.
  const [cornerRadius, setCornerRadius] = useState<number | null>(null);
  const socketRef = useRef<WebSocket | null>(null);
  const pending = useRef(new Map<string, PendingCall>());
  const pings = useRef(new Map<string, number>());
  const nextId = useRef(0);
  const nextPing = useRef(0);
  const attempt = useRef(0);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Object URLs behind the picture on screen and the one it replaced; the
  // older one is released only once a newer picture has taken over.
  const sources = useRef<{ shown: string | null; older: string | null }>({
    shown: null,
    older: null,
  });
  const frameTimes = useRef<number[]>([]);
  const lastFrameAt = useRef<number | null>(null);
  const connectedRef = useRef(false);
  /** When the current socket opened, to notice a first frame that never comes. */
  const connectedAt = useRef<number | null>(null);
  /** Server clock minus this browser's clock, from the hello and each pong. */
  const serverOffset = useRef<number | null>(null);
  // Bumped by retry() to tear the effect down and start a fresh socket now.
  const [retryNonce, setRetryNonce] = useState(0);

  const flushPending = useCallback((message: string) => {
    pending.current.forEach((call) => {
      clearTimeout(call.timer);
      call.reject(new Error(message));
    });
    pending.current.clear();
    pings.current.clear();
  }, []);

  const currentFps = () => {
    const now = Date.now();
    const times = frameTimes.current;
    while (times.length && now - times[0] > FPS_WINDOW_MS) times.shift();
    return times.length / (FPS_WINDOW_MS / 1000);
  };

  const showFrame = useCallback((src: string, header: FrameHeader | null) => {
    const now = Date.now();
    releaseSource(sources.current.older);
    sources.current = { shown: src, older: sources.current.shown };
    setFrame(src);
    frameTimes.current.push(now);
    lastFrameAt.current = now;
    let lag: number | null = null;
    if (header?.captured_server_ms && serverOffset.current !== null) {
      lag = Math.max(0, now + serverOffset.current - header.captured_server_ms);
    }
    const fps = currentFps();
    setStats((s) => ({ ...s, fps, frameLagMs: lag ?? s.frameLagMs, lastFrameAt: now }));
    setStalled(false);
    if (typeof header?.corner_radius === "number") setCornerRadius(header.corner_radius);
  }, []);

  // Another phone, another shape: forget what the last one reported.
  useEffect(() => {
    setCornerRadius(null);
    setCapture(null);
    setFirstFrameLate(false);
    setDeviceOffline(false);
  }, [deviceId]);

  useEffect(() => {
    let disposed = false;
    attempt.current = 0;
    let pingTimer: ReturnType<typeof setInterval> | null = null;

    const clearTimer = () => {
      if (timer.current !== null) {
        clearTimeout(timer.current);
        timer.current = null;
      }
    };

    const stopPinging = () => {
      if (pingTimer !== null) {
        clearInterval(pingTimer);
        pingTimer = null;
      }
    };

    const ping = () => {
      const socket = socketRef.current;
      if (!socket || socket.readyState !== WebSocket.OPEN) return;
      const now = Date.now();
      // Forget pings that never came back rather than let the map grow.
      pings.current.forEach((sentAt, key) => {
        if (now - sentAt > PING_TIMEOUT_MS) pings.current.delete(key);
      });
      const id = `p${nextPing.current++}`;
      pings.current.set(id, now);
      try {
        socket.send(JSON.stringify({ id, cmd: "ping" }));
      } catch {
        pings.current.delete(id);
      }
    };

    const scheduleReconnect = () => {
      if (disposed) return;
      setConnected(false);
      connectedRef.current = false;
      setReconnecting(true);
      const delay = Math.min(BASE_DELAY_MS * 2 ** attempt.current, MAX_DELAY_MS);
      // Jitter so a server restart does not reconnect every panel in lockstep.
      const backoff = delay / 2 + Math.random() * (delay / 2);
      attempt.current += 1;
      timer.current = setTimeout(connect, backoff);
    };

    const connect = () => {
      if (disposed) return;
      clearTimer();
      const socket = new WebSocket(websocketUrl(`/ws/ui/device/${deviceId}`));
      socket.binaryType = "arraybuffer";
      socketRef.current = socket;

      socket.onopen = () => {
        if (disposed) return;
        attempt.current = 0;
        setConnected(true);
        connectedRef.current = true;
        connectedAt.current = Date.now();
        setFirstFrameLate(false);
        setReconnecting(false);
        setDeviceOffline(false);
        setError(null);
        ping();
        pingTimer = setInterval(ping, PING_INTERVAL_MS);
      };
      // onclose always follows onerror, so the error handler stays quiet and
      // the close code decides what the panel tells the user.
      socket.onclose = (event) => {
        stopPinging();
        flushPending("Panel is not connected");
        connectedAt.current = null;
        if (disposed) return;
        if (event.code === 4401) {
          // Retrying with the same token cannot succeed.
          setConnected(false);
          connectedRef.current = false;
          setReconnecting(false);
          setError("Not authorised for this device");
          return;
        }
        if (event.code === 4403) {
          // The account is closed, and the code does not say which of the
          // three it is — expired, suspended, or the console blocked — so the
          // sentence must be true of all of them. The next request the page
          // makes gets the coded 403 and the login screen says the rest.
          // Retrying would redial for ever against a door that is shut.
          setConnected(false);
          connectedRef.current = false;
          setReconnecting(false);
          setError("Access to this account is closed — sign in again for details");
          return;
        }
        if (event.code === 4409) setDeviceOffline(true);
        scheduleReconnect();
      };
      socket.onmessage = (event) => {
        if (event.data instanceof ArrayBuffer) {
          const decoded = decodeLiveFrame(event.data);
          if (decoded) showFrame(frameSource(decoded.jpeg), decoded.header);
          return;
        }
        let message: {
          type?: string;
          event?: string;
          id?: string;
          ok?: boolean;
          error?: string;
          server_time_ms?: number;
          capture?: CaptureState;
          available?: boolean;
          backend?: string;
          reason?: string;
          message?: string;
          max_fps?: number | null;
          inferred?: boolean;
          data?: { image_b64?: string; jpeg_b64?: string; mime?: string };
        };
        try {
          message = JSON.parse(event.data);
        } catch {
          return;
        }
        if (message.type === "hello") {
          // A first estimate; each pong refines it with half a round trip.
          if (typeof message.server_time_ms === "number" && serverOffset.current === null) {
            serverOffset.current = message.server_time_ms - Date.now();
          }
          if (message.capture && typeof message.capture.reason === "string") {
            setCapture(message.capture);
          }
          return;
        }
        if (message.type === "capture" && typeof message.reason === "string") {
          // The phone's situation changed: consent given, screen went dark…
          setCapture({
            available: Boolean(message.available),
            backend: message.backend ?? "none",
            reason: message.reason,
            message: message.message ?? "",
            max_fps: message.max_fps ?? null,
            inferred: false,
          });
          return;
        }
        if (message.type === "pong" && message.id) {
          const sentAt = pings.current.get(message.id);
          pings.current.delete(message.id);
          if (sentAt === undefined) return;
          const now = Date.now();
          const rtt = now - sentAt;
          if (typeof message.server_time_ms === "number") {
            serverOffset.current = message.server_time_ms - (sentAt + rtt / 2);
          }
          setStats((s) => ({ ...s, serverRttMs: rtt }));
          return;
        }
        if (message.type === "event" && message.event === "frame") {
          // An older server still sending base64 JSON frames.
          const data = message.data ?? {};
          const encoded = data.image_b64 ?? data.jpeg_b64;
          const mime = data.mime ?? "image/jpeg";
          if (encoded) showFrame(`data:${mime};base64,${encoded}`, null);
          return;
        }
        if (message.type === "result" && message.id) {
          const call = pending.current.get(message.id);
          pending.current.delete(message.id);
          if (!call) return;
          clearTimeout(call.timer);
          const rtt = Date.now() - call.sentAt;
          setStats((s) => ({ ...s, commandRttMs: rtt }));
          if (message.ok) call.resolve(message.data);
          else call.reject(new Error(message.error ?? "Device refused the command"));
        }
      };
    };

    connect();

    // Frame rate decays to zero on its own, and a picture that stops
    // arriving while the socket is still open is reported as a stall
    // rather than left looking live.
    const tick = setInterval(() => {
      const fps = currentFps();
      setStats((s) => (s.fps === fps ? s : { ...s, fps }));
      const last = lastFrameAt.current;
      setStalled(
        connectedRef.current && last !== null && Date.now() - last > STALL_AFTER_MS,
      );
      const opened = connectedAt.current;
      setFirstFrameLate(
        connectedRef.current &&
          last === null &&
          opened !== null &&
          Date.now() - opened > FIRST_FRAME_TIMEOUT_MS,
      );
    }, TICK_MS);

    // A sleeping laptop or dropped Wi-Fi fires `online` long before the next
    // backoff tick — reconnect the moment the network is back.
    const onOnline = () => {
      if (disposed || socketRef.current?.readyState === WebSocket.OPEN) return;
      attempt.current = 0;
      clearTimer();
      connect();
    };
    window.addEventListener("online", onOnline);

    return () => {
      disposed = true;
      clearTimer();
      stopPinging();
      clearInterval(tick);
      window.removeEventListener("online", onOnline);
      socketRef.current?.close();
      socketRef.current = null;
      flushPending("Panel closed");
    };
  }, [deviceId, retryNonce, flushPending, showFrame]);

  // The last picture survives reconnects on purpose; its object URLs are
  // released only when the panel itself goes away.
  useEffect(
    () => () => {
      releaseSource(sources.current.shown);
      releaseSource(sources.current.older);
      sources.current = { shown: null, older: null };
    },
    [],
  );

  const send = useCallback(
    (cmd: string, args: Record<string, unknown> = {}) => {
      const socket = socketRef.current;
      if (!socket || socket.readyState !== WebSocket.OPEN) {
        return Promise.reject(new Error("Panel is not connected"));
      }
      const id = `c${nextId.current++}`;
      return new Promise<unknown>((resolve, reject) => {
        const timer = setTimeout(() => {
          const call = pending.current.get(id);
          if (!call) return;
          pending.current.delete(id);
          call.reject(new Error("Device did not answer in time"));
        }, COMMAND_TIMEOUT_MS);
        pending.current.set(id, { resolve, reject, timer, sentAt: Date.now() });
        try {
          socket.send(JSON.stringify({ id, cmd, args }));
        } catch {
          // The socket closed between the readyState check and the send.
          pending.current.delete(id);
          clearTimeout(timer);
          reject(new Error("Panel is not connected"));
        }
      });
    },
    [],
  );

  const retry = useCallback(() => {
    setConnected(false);
    setRetryNonce((nonce) => nonce + 1);
  }, []);

  return {
    frame,
    cornerRadius,
    connected,
    reconnecting,
    deviceOffline,
    stalled,
    error,
    stats,
    capture,
    firstFrameLate,
    send,
    retry,
  };
}
